import { IComboBox, IComboBoxOption } from '@fluentui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { TokenWorkspaceFiltersResult, WorkspaceFilterOption } from '../types';

/**
 * Custom hook to manage workspace filter state for H2OGPTe Token Usage
 *
 * Manages the selection state for workspace filters in the token usage context.
 * Filter options are provided from usePrefetchedTokenFilterOptions which extracts
 * them from prefetched WORKSPACE-grouped token data.
 *
 * The hook maintains two states:
 * - pendingWorkspaces: UI state that updates immediately on selection
 * - appliedWorkspaces: Committed state that triggers data fetches
 *
 * When date range changes, both pending and applied filters are automatically reset.
 *
 * @param workspaceOptions Filter options extracted from token workspace usage data
 * @param startTime Start of the current time range (for change detection)
 * @param endTime End of the current time range (for change detection)
 * @param granularity Granularity of the current time range (for change detection)
 * @returns Object containing workspace options, pending/applied workspaces, handlers, and computed states
 */
export function useTokenWorkspaceFilters(
  workspaceOptions: WorkspaceFilterOption[],
  startTime: Date,
  endTime: Date,
  granularity: Granularity
): TokenWorkspaceFiltersResult {
  // Pending state: immediate UI updates, no fetch triggered
  const [pendingWorkspaces, setPendingWorkspaces] = useState<string[]>([]);

  // Applied state: committed filters that trigger data fetches
  const [appliedWorkspaces, setAppliedWorkspaces] = useState<string[]>([]);

  // Track previous date range to detect changes
  const prevDateRangeRef = useRef<string>('');
  const currentDateRangeKey = `${startTime.toISOString()}-${endTime.toISOString()}-${granularity}`;

  // Auto-reset filters when date range changes
  useEffect(() => {
    if (prevDateRangeRef.current !== '' && prevDateRangeRef.current !== currentDateRangeKey) {
      setPendingWorkspaces([]);
      setAppliedWorkspaces([]);
    }
    prevDateRangeRef.current = currentDateRangeKey;
  }, [currentDateRangeKey]);

  // Handle workspace filter changes (updates pending state only, no fetch)
  const handleChange = useCallback((_event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
    if (!option) return;

    setPendingWorkspaces((prev) => {
      const updatedWorkspaces = option.selected
        ? [...prev, option.key as string]
        : prev.filter((ws) => ws !== option.key);

      return updatedWorkspaces;
    });
  }, []);

  // Apply handler: commits pending changes to applied state (triggers fetch)
  const handleApply = useCallback(() => {
    setAppliedWorkspaces(pendingWorkspaces);
  }, [pendingWorkspaces]);

  // Reset handler: implements two scenarios
  // 1. If no applied filters: clear pending only (no fetch)
  // 2. If applied filters exist: clear both pending and applied (triggers fetch)
  const handleReset = useCallback(() => {
    setPendingWorkspaces([]);

    // Only clear applied filters if there were any (triggers fetch only if needed)
    if (appliedWorkspaces.length > 0) {
      setAppliedWorkspaces([]);
    }
  }, [appliedWorkspaces]);

  // Computed values for button states
  const hasChanges = JSON.stringify([...pendingWorkspaces].sort()) !== JSON.stringify([...appliedWorkspaces].sort());
  const hasFiltersToReset = pendingWorkspaces.length > 0 || appliedWorkspaces.length > 0;

  return {
    workspaceOptions,
    selectedWorkspaces: pendingWorkspaces,
    appliedWorkspaces,
    handleChange,
    handleApply,
    handleReset,
    hasChanges,
    hasFiltersToReset,
  };
}
